import { useTranslation } from 'react-i18next'

/** "1,500 RON" — the big card value and the delta below it read the same way. */
export function formatCurrency(value: number, currency: string): string {
  const amount = value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
  return `${amount} ${currency}`
}

function trendBadge(
  current: number,
  previous: number,
  formatDelta: (value: number) => string,
): { label: string; positive: boolean } | null {
  if (previous === 0 && current === 0) return null
  // A percentage against an empty baseline is a division by zero. It used to
  // render as "+∞%", which reads as a bug and buries the only fact worth
  // having, so the absolute change is shown instead. This is no longer a rare
  // corner: now that the comparison covers just the elapsed days of the
  // previous period, the first days of a month often have nothing in them.
  if (previous === 0) return { label: `+${formatDelta(current)}`, positive: true }
  const pct = ((current - previous) / previous) * 100
  return { label: `${pct >= 0 ? '+' : ''}${Math.round(pct)}%`, positive: pct >= 0 }
}

export function TrendSpan({
  current,
  previous,
  compareLabel,
  partial,
  formatDelta = String,
}: {
  current: number
  previous: number
  compareLabel: string
  /** How to render an absolute change, for the cards whose value carries a unit. */
  formatDelta?: (value: number) => string
  /**
   * The compare period was cut short to match how much of this one has elapsed,
   * so the label says "so far". The app and the Worker deploy as separate jobs;
   * an API that predates the field sends undefined, which reads as a settled
   * comparison and is the right thing to show for one.
   */
  partial: boolean
}) {
  const { t } = useTranslation()
  const trend = trendBadge(current, previous, formatDelta)
  if (!trend) return <span className="text-xs text-foreground/40">{t('statistics.noChange')}</span>
  return (
    <span className={`text-xs font-medium ${trend.positive ? 'text-success' : 'text-danger'}`}>
      {trend.label}{' '}
      {t(partial ? 'statistics.vsLabelPartial' : 'statistics.vsLabel', { period: compareLabel })}
    </span>
  )
}
